import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ChevronLeft,
  Package,
  Plus,
  Radio,
  X,
  Boxes,
  Upload,
  Image as ImageIcon,
  Search,
  Clipboard,
  FileSpreadsheet,
} from 'lucide-react';
import axios from 'axios';
import InventoryOverview from './InventoryOverview';
import { LiveDeliveriesModal } from './LiveDeliveriesModal';
import { PackagesInFastmax } from './PackagesInFastmax';
import BulkProductUploadModal from '../../shared/inventory/BulkProductUploadModal';

function generateSkuPreview(title) {
  const normalized = title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 36);
  return normalized || 'SKU-AUTOMATICO';
}

function Inventory({ userId, setActiveComponent }) {
  const [categories, setCategories] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [fastmaxModalOpen, setFastmaxModalOpen] = useState(false);
  const [liveDeliveriesOpen, setLiveDeliveriesOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [activeForm, setActiveForm] = useState(null); // 'product' | 'reception'
  const [refreshKey, setRefreshKey] = useState(0);

  const [title, setTitle] = useState('');
  const [sku, setSku] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [selectedCats, setSelectedCats] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [prodLoading, setProdLoading] = useState(false);

  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [receptionQty, setReceptionQty] = useState('');
  const [receptionNotes, setReceptionNotes] = useState('');
  const [receptionLoading, setReceptionLoading] = useState(false);

  const fileInputRef = useRef(null);
  const baseUrl = process.env.REACT_APP_API_URL || '';

  const skuPreview = useMemo(() => generateSkuPreview(title), [title]);

  useEffect(() => {
    if (!userId) return;
    fetchCategories();
    fetchCatalogItems();
  }, [userId, refreshKey]);

  async function fetchCategories() {
    try {
      const { data } = await axios.get(`${baseUrl}/api/categories`);
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  async function fetchCatalogItems() {
    if (!userId) return;
    try {
      const { data } = await axios.get(`${baseUrl}/api/inventory`, {
        params: { user_id: userId }
      });
      setCatalogItems(data.items || []);
    } catch (error) {
      console.error('Error fetching catalog items:', error);
    }
  }

  function notifyInventoryChanged() {
    setRefreshKey((value) => value + 1);
  }

  function addFiles(files) {
    const images = Array.from(files).filter((file) => {
      const valid = file.type.startsWith('image/');
      if (!valid) alert(`${file.name} no es una imagen valida`);
      return valid;
    });
    if (!images.length) return;

    setSelectedFiles((prev) => [...prev, ...images]);
    images.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrls((prev) => [...prev, reader.result]);
      reader.readAsDataURL(file);
    });
  }

  function handlePaste(e) {
    const files = [];
    for (const item of e.clipboardData?.items || []) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length) addFiles(files);
  }

  function removeImage(index) {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  }

  async function uploadProductImages(productId) {
    if (!selectedFiles.length) return;
    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append('files', file));
    formData.append('is_primary', 'true');
    formData.append('primary_image_index', '0');

    await axios.post(`${baseUrl}/api/products/${productId}/images/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async function handleCreateProduct() {
    if (!title.trim() || !description.trim()) {
      alert('Titulo y descripcion son obligatorios');
      return;
    }

    setProdLoading(true);
    try {
      const payload = {
        title: title.trim(),
        sku: sku.trim(),
        description: description.trim(),
        price: price === '' ? 0 : Number(price),
        user_id: userId,
        category_ids: selectedCats.map((cat) => cat.id)
      };

      const { data } = await axios.post(`${baseUrl}/api/create_product`, payload);
      try {
        await uploadProductImages(data.id);
      } catch (uploadError) {
        console.error('Error uploading product images:', uploadError);
        alert('Producto creado, pero algunas imagenes no pudieron subirse.');
      }

      setTitle('');
      setSku('');
      setDescription('');
      setPrice('');
      setSelectedCats([]);
      setSelectedFiles([]);
      setPreviewUrls([]);
      setActiveForm(null);
      notifyInventoryChanged();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.error || 'Error al crear producto');
    } finally {
      setProdLoading(false);
    }
  }

  async function handleCreateReceptionRequest() {
    if (!selectedProduct || !receptionQty) {
      alert('Selecciona un producto y una cantidad');
      return;
    }

    setReceptionLoading(true);
    try {
      await axios.post(`${baseUrl}/api/inventory/reception-requests`, {
        user_id: userId,
        product_id: selectedProduct.product_id,
        quantity: Number(receptionQty),
        notes: receptionNotes.trim(),
      });

      setSelectedProduct(null);
      setReceptionQty('');
      setReceptionNotes('');
      setProductSearch('');
      setActiveForm(null);
      notifyInventoryChanged();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.error || 'Error al crear solicitud de recepcion');
    } finally {
      setReceptionLoading(false);
    }
  }

  function toggleCategorySelection(category) {
    if (selectedCats.some((cat) => cat.id === category.id)) {
      setSelectedCats(selectedCats.filter((cat) => cat.id !== category.id));
    } else {
      setSelectedCats([...selectedCats, category]);
    }
  }

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return catalogItems.slice(0, 12);
    return catalogItems.filter((item) =>
      item.title?.toLowerCase().includes(q) ||
      item.sku?.toLowerCase().includes(q)
    ).slice(0, 12);
  }, [catalogItems, productSearch]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveComponent('Options')}
              className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">Volver</span>
            </button>

            <h1 className="text-lg sm:text-xl font-extrabold text-[#B1336E] font-montserrat">
              Inventario Fast Max
            </h1>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setLiveDeliveriesOpen(true)}
                className="p-2 text-[#B1336E] hover:bg-[#B1336E]/10 rounded-lg transition-colors relative"
                title="Seguimiento en vivo"
              >
                <Radio className="w-5 h-5" />
                <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#67A43C] animate-pulse" />
              </button>
              <button
                onClick={() => setFastmaxModalOpen(true)}
                className="p-2 text-[#B1336E] hover:bg-[#B1336E]/10 rounded-lg transition-colors"
                title="Paquetes en Fast Max"
              >
                <Boxes className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveForm(activeForm === 'product' ? null : 'product')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all duration-200 font-montserrat text-sm ${
                activeForm === 'product'
                  ? 'bg-[#B1336E] text-white shadow-md'
                  : 'bg-white text-slate-700 border border-slate-300 hover:border-[#B1336E] hover:text-[#B1336E]'
              }`}
            >
              <Package className="w-4 h-4" />
              <span>Nuevo Producto</span>
            </button>

            <button
              onClick={() => setBulkUploadOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all duration-200 font-montserrat text-sm bg-white text-slate-700 border border-slate-300 hover:border-[#B1336E] hover:text-[#B1336E]"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span>Carga Masiva</span>
            </button>

            <button
              onClick={() => setActiveForm(activeForm === 'reception' ? null : 'reception')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all duration-200 font-montserrat text-sm ${
                activeForm === 'reception'
                  ? 'bg-[#67A43C] text-white shadow-md'
                  : 'bg-white text-slate-700 border border-slate-300 hover:border-[#67A43C] hover:text-[#67A43C]'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span>Enviar Stock a Fast Max</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {activeForm === 'product' && (
          <div className="my-6 bg-white rounded-2xl shadow-sm border-2 border-[#B1336E] p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#B1336E] font-montserrat">Crear Producto</h3>
              <button onClick={() => setActiveForm(null)} className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <input
                  type="text"
                  placeholder="Titulo del producto *"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm"
                />
                <p className="mt-1 text-xs text-slate-500 font-montserrat">SKU automatico si lo dejas vacio: {skuPreview}</p>
              </div>

              <input
                type="text"
                placeholder="SKU opcional"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                className="px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm"
              />

              <input
                type="number"
                placeholder="Precio opcional"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm"
              />

              <textarea
                placeholder="Descripcion *"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows="2"
                className="sm:col-span-2 lg:col-span-3 px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm resize-none"
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">Categorias opcionales</label>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => toggleCategorySelection(cat)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-montserrat transition-colors ${
                      selectedCats.some((c) => c.id === cat.id)
                        ? 'bg-[#B1336E] text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div
              className={`mt-5 border-2 border-dashed rounded-xl p-5 transition-colors ${
                dragActive ? 'border-[#B1336E] bg-[#B1336E]/5' : 'border-slate-300 bg-slate-50'
              }`}
              onPaste={handlePaste}
              onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                addFiles(e.dataTransfer.files);
              }}
              tabIndex={0}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
              />
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#B1336E]/10 flex items-center justify-center">
                    <ImageIcon className="w-5 h-5 text-[#B1336E]" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800 font-montserrat">Imagenes opcionales</p>
                    <p className="text-xs text-slate-500 font-montserrat flex items-center gap-1">
                      <Clipboard className="w-3 h-3" />
                      Arrastra, pega o selecciona desde tus archivos
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-xl text-sm font-semibold text-slate-700 hover:border-[#B1336E] hover:text-[#B1336E] font-montserrat"
                >
                  <Upload className="w-4 h-4" />
                  Seleccionar
                </button>
              </div>

              {previewUrls.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-4">
                  {previewUrls.map((url, index) => (
                    <div key={url} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-white">
                      <img src={url} alt={`Producto ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 p-1 bg-black/60 text-white rounded-md hover:bg-black"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={handleCreateProduct}
              disabled={prodLoading}
              className="mt-4 w-full bg-[#B1336E] hover:bg-[#B1336E]/90 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed font-montserrat"
            >
              {prodLoading ? 'Guardando...' : 'Guardar Producto'}
            </button>
          </div>
        )}

        {activeForm === 'reception' && (
          <div className="my-6 bg-white rounded-2xl shadow-sm border-2 border-[#67A43C] p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#67A43C] font-montserrat">Solicitud de Recepcion</h3>
              <button onClick={() => setActiveForm(null)} className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Buscar producto por nombre o SKU..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#67A43C]/20 focus:border-[#67A43C] font-montserrat text-sm"
                  />
                </div>

                <div className="mt-3 max-h-56 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100">
                  {filteredProducts.length === 0 ? (
                    <div className="p-4 text-sm text-slate-500 font-montserrat">No se encontraron productos</div>
                  ) : (
                    filteredProducts.map((item) => (
                      <button
                        key={item.product_id}
                        type="button"
                        onClick={() => setSelectedProduct(item)}
                        className={`w-full text-left p-3 hover:bg-slate-50 transition-colors ${
                          selectedProduct?.product_id === item.product_id ? 'bg-[#67A43C]/10' : 'bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-900 font-montserrat">{item.title}</p>
                            <p className="text-xs text-slate-500 font-montserrat">{item.sku}</p>
                          </div>
                          <span className="text-xs font-bold text-[#67A43C] bg-[#67A43C]/10 rounded-full px-2 py-1">
                            Fast Max: {item.fastmax_quantity || 0}
                          </span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-slate-500 font-montserrat mb-1">Producto seleccionado</p>
                  <p className="text-sm font-bold text-slate-800 font-montserrat">
                    {selectedProduct ? selectedProduct.title : 'Selecciona un producto'}
                  </p>
                  {selectedProduct && (
                    <p className="text-xs text-slate-500 font-montserrat">{selectedProduct.sku}</p>
                  )}
                </div>
                <input
                  type="number"
                  placeholder="Cantidad *"
                  value={receptionQty}
                  min="1"
                  onChange={(e) => setReceptionQty(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#67A43C]/20 focus:border-[#67A43C] font-montserrat text-sm"
                />
                <textarea
                  placeholder="Nota opcional para Fast Max"
                  value={receptionNotes}
                  onChange={(e) => setReceptionNotes(e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#67A43C]/20 focus:border-[#67A43C] font-montserrat text-sm resize-none"
                />
                <button
                  onClick={handleCreateReceptionRequest}
                  disabled={receptionLoading || !selectedProduct || !receptionQty}
                  className="w-full bg-[#67A43C] hover:bg-[#67A43C]/90 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed font-montserrat"
                >
                  {receptionLoading ? 'Enviando...' : 'Crear Solicitud'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <InventoryOverview userId={userId} refreshKey={refreshKey} onInventoryChanged={notifyInventoryChanged} />

      <PackagesInFastmax
        isOpen={fastmaxModalOpen}
        onClose={() => setFastmaxModalOpen(false)}
        userId={userId}
      />

      <LiveDeliveriesModal
        isOpen={liveDeliveriesOpen}
        onClose={() => setLiveDeliveriesOpen(false)}
        userId={userId}
      />

      <BulkProductUploadModal
        isOpen={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        userId={userId}
        categories={categories}
        onProductsCreated={notifyInventoryChanged}
      />

    </div>
  );
}

export default Inventory;
